import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, Calendar, Plus, Clock, CheckCircle2, 
  Edit2, Trash2, Eye, Zap, Filter, Image as ImageIcon,
  Video, Type, Facebook, Instagram, Twitter, Youtube
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const DailyDropSchedule = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, scheduled, published, draft

  useEffect(() => {
    fetchPosts();
  }, [filter]);

  const fetchPosts = async () => {
    try {
      const response = await api.get(`/daily-drop/posts?status=${filter}`);
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
      // Use mock data for now
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await api.delete(`/daily-drop/posts/${postId}`);
      toast.success('Post deleted');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      published: 'bg-green-500/20 text-green-300 border-green-500/30',
      scheduled: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      draft: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
    };
    return styles[status] || styles.draft;
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      facebook: Facebook,
      instagram: Instagram,
      twitter: Twitter,
      youtube: Youtube,
      tiktok: () => <span className="text-xs">TT</span>
    };
    const Icon = icons[platform] || Facebook;
    return <Icon className="h-3 w-3" />;
  };

  const getPostTypeIcon = (type) => {
    const icons = {
      image: ImageIcon,
      video: Video,
      text: Type
    };
    const Icon = icons[type] || ImageIcon;
    return <Icon className="h-4 w-4 text-purple-400" />;
  };

  const filters = [
    { id: 'all', label: 'All Posts' },
    { id: 'scheduled', label: 'Scheduled' },
    { id: 'published', label: 'Published' },
    { id: 'draft', label: 'Drafts' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      {/* Header */}
      <header className="border-b border-purple-500/20 bg-black/30 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              className="text-purple-200 hover:text-white hover:bg-purple-500/20"
              onClick={() => navigate('/daily-drop')}
              data-testid="back-button"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Calendar className="h-5 w-5 text-purple-400" />
                Scheduled Posts
              </h1>
              <p className="text-xs text-purple-300">Manage your content calendar</p>
            </div>
          </div>
          <Button
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
            onClick={() => navigate('/daily-drop/create')}
            data-testid="create-post-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Post
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <Filter className="h-4 w-4 text-purple-400" />
          <div className="flex gap-2">
            {filters.map((f) => (
              <Button
                key={f.id}
                variant={filter === f.id ? 'default' : 'outline'}
                size="sm"
                className={filter === f.id 
                  ? 'bg-purple-600 hover:bg-purple-500' 
                  : 'border-purple-500/30 text-purple-200 hover:bg-purple-500/20'}
                onClick={() => setFilter(f.id)}
                data-testid={`filter-${f.id}`}
              >
                {f.label}
              </Button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
          </div>
        ) : posts.length === 0 ? (
          <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
            <CardContent className="py-16 text-center">
              <div className="bg-purple-500/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="h-10 w-10 text-purple-400" />
              </div>
              <h3 className="text-white font-semibold text-lg mb-2">No posts yet</h3>
              <p className="text-purple-300/70 mb-6">Start creating content to build your posting schedule</p>
              <Button
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
                onClick={() => navigate('/daily-drop/create')}
                data-testid="empty-create-button"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Post
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {posts.map((post) => (
              <Card 
                key={post.id} 
                className="bg-black/30 border-purple-500/20 backdrop-blur-sm hover:border-purple-500/40 transition-all"
                data-testid={`post-card-${post.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    {/* Thumbnail */}
                    <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-purple-500/10">
                      {post.media_url ? (
                        <img 
                          src={post.media_url} 
                          alt="" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          {getPostTypeIcon(post.post_type)}
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <p className="text-white font-medium truncate">
                            {post.caption?.substring(0, 80) || 'No caption'}...
                          </p>
                          <div className="flex items-center gap-3 mt-2">
                            <Badge className={getStatusBadge(post.status)}>
                              {post.status === 'published' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                              {post.status === 'scheduled' && <Clock className="h-3 w-3 mr-1" />}
                              {post.status}
                            </Badge>
                            <span className="text-purple-300/60 text-sm flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(post.scheduled_for || post.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          {/* Platforms */}
                          <div className="flex items-center gap-2 mt-2">
                            {post.platforms?.map((p) => (
                              <div 
                                key={p} 
                                className="bg-purple-500/20 p-1.5 rounded"
                                title={p}
                              >
                                {getPlatformIcon(p)}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-purple-300 hover:text-white hover:bg-purple-500/20"
                            title="Preview"
                            data-testid={`preview-${post.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-purple-300 hover:text-white hover:bg-purple-500/20"
                            title="Edit"
                            data-testid={`edit-${post.id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                            onClick={() => handleDelete(post.id)}
                            title="Delete"
                            data-testid={`delete-${post.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Calendar View Teaser */}
        <Card className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 border-purple-500/30 backdrop-blur-sm mt-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-purple-500/30 p-3 rounded-xl">
                  <Calendar className="h-6 w-6 text-purple-300" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Calendar View</h3>
                  <p className="text-purple-300/70 text-sm">Visual calendar coming in Pro plan</p>
                </div>
              </div>
              <Button
                variant="outline"
                className="border-purple-500/50 text-purple-200 hover:bg-purple-500/20"
                onClick={() => navigate('/subscription')}
                data-testid="upgrade-calendar-button"
              >
                <Zap className="h-4 w-4 mr-2" />
                Upgrade
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default DailyDropSchedule;
